// HolidaySetup
import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { Edit, Trash, Calendar } from "lucide-react";
import { HOLIDAY_SETUP } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { DatePicker, Select } from "antd";
import { MasterRecordStatusChip } from "../../utils/masterRecordStatus";
import { holidaytype } from "../../constants/enum";
import dayjs from "dayjs";
import { configBranchLabel, withConfigBranch } from "../../utils/branchSession";

const HolidaySetup = () => {
    const { create, update } = useApiMethods(); // Added remove for delete operations
    const [HolidaySetupsData, setHolidaySetupsData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const { add: addPermission, edit: editPermission, draft: draftPermission } = checkUserPermissionFunc('configuration', 'holiday', 'SubMenu')

    // Form state to match holiday fields
    const [formData, setFormData] = useState({
        code: "",
        holiday_name: "",
        holiday_type: undefined,
        date: null,
        notes: "",
        is_active: true,
        is_draft: false,
        is_recurring: false
    });

    // Reset form function
    const resetForm = () => {
        setFormData({
            code: "",
            holiday_name: "",
            holiday_type: "",
            date: null,
            notes: "",
            is_active: true,
            is_draft: false,
            is_recurring: false
        });
        setEditMode(false);
        setEditId(null);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    // Table columns for holiday data
    const HolidaySetupColumns = [
        { accessor: "code", Header: "Holiday Code", sortable: true, filterable: true },
        {
            accessor: "branch_name",
            Header: "Branch",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "holiday_name",
            Header: "Holiday Name",
            sortable: true,
            filterable: true,
        },
        {
            accessor: "holiday_type",
            Header: "Holiday Type",
            sortable: true,
            filterable: true,
            filterType: "select",
            filterOptions: holidaytype.map((item) => ({ value: item.value, label: item.text })),
            Cell: ({ value }) => {
                const type = holidaytype.find(type => type.value === value);
                return type ? type.text : '-';
            }
        },
        {
            accessor: "date",
            Header: "Date",
            sortable: true,
            filterable: true,
            filterType: "date",
            Cell: ({ value, row }) => (
                <div className="flex items-center gap-2">
                    <span>{value ? dayjs(value).format('DD-MM-YYYY') : '-'}</span>
                    {row?.is_recurring && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            Yearly
                        </span>
                    )}
                </div>
            )
        },
        {
            accessor: "notes",
            Header: "Note",
            Cell: ({ value }) => truncateText(value, 25, "300px", "balance", "No Description")
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value, row }) => (
                <MasterRecordStatusChip
                    isActive={!!value}
                    isDraft={!!row?.original?.is_draft}
                />
            ),
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Actions",
                sortable: false,
                Cell: ({ row }) => {
                    const handleOpenDialog = () => {
                        setEditMode(true);
                        setEditId(row?.code);
                        setFormData({
                            id: row?.id || null,
                            code: row?.code || "",
                            holiday_name: row?.holiday_name || "",
                            holiday_type: row?.holiday_type || "",
                            date: row?.date ? dayjs(row.date) : null,
                            notes: row?.notes || "",
                            is_active: row?.is_active ?? true,
                            is_draft: row?.is_draft || false,
                            is_recurring: row?.is_recurring || false
                        });
                        setOpenDialog(true);
                    };

                    const handleDeleteClick = (row) => {
                        setSelectedRow(row);
                        setIsDeleteConfirmOpen(true);
                    };

                    return (
                        <div className="flex items-center gap-2">
                            {/* Edit Button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenDialog}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>

                            {/* Delete Button (only if row is draft) */}
                            {row?.is_draft && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                    onClick={() => handleDeleteClick(row)}
                                >
                                    <Trash className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ] : [])
    ];

    // Save as draft function
    const handleDraft = async () => {
        try {
            setLoading(true);
            const payload = withConfigBranch({
                ...formData,
                is_draft: true,
                date: formData.date ? formData.date.format('YYYY-MM-DD') : null
            });

            // Use HOLIDAY_SETUP 
            const response = await create(HOLIDAY_SETUP, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Holiday saved to draft successfully");
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to save draft");
            }
        } catch (error) {
            console.error("Error saving draft:", error);
            ToastMsg("error", "Failed to save draft");
        } finally {
            setLoading(false);
        }
    };

    // Delete draft function
    const handleDraftDelete = async () => {
        try {
            setLoading(true);
            if (!selectedRow) return;

            // Use JSON payload
            const payload = {
                id: selectedRow.id,
                is_active: false,
                draft_delete: true,
                is_draft: true
            };

            // Make the API call
            const response = await update(HOLIDAY_SETUP, payload);

            // Check for success status properly
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Draft holiday deleted successfully");
                setServerTrigger((prev) => !prev);
                setIsDeleteConfirmOpen(false);
                setSelectedRow(null);
            } else {
                ToastMsg("error", response?.message || "Failed to delete draft holiday");
            }
        } catch (error) {
            console.error("Error deleting draft:", error);
            ToastMsg("error", "Failed to delete draft holiday");
        } finally {
            setLoading(false);
        }
    };

    // Save or update function
    const handleSave = async () => {
        try {
            setLoading(true);
            let payload = withConfigBranch({
                ...formData,
                is_draft: false,
                date: formData.date ? formData.date.format('YYYY-MM-DD') : null
            });

            let response;

            if (editMode && formData.id) {
                // For update
                response = await update(HOLIDAY_SETUP, payload);
            } else {
                // For create
                response = await create(HOLIDAY_SETUP, payload);
            }

            if (response?.status === "success") {
                ToastMsg("success", response.message || `Holiday ${editMode ? 'updated' : 'saved'} successfully`);
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || `Failed to ${editMode ? 'update' : 'save'} holiday`);
            }
        } catch (error) {
            console.error("Error saving holiday:", error);
            ToastMsg("error", `Failed to ${editMode ? 'update' : 'save'} holiday`);
        } finally {
            setLoading(false);
        }
    };

    // Handle form field changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <ContainerCard>
            {/* Header with holiday-specific content */}
            <ModuleTitleCard
                Icon={Calendar}
                title={'Holiday Setup'}
                subtitle={`Manage holidays for ${configBranchLabel()}. A holiday saved here applies only to this branch.`}
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={setOpenDialog}
                buttonName={'Add Holiday'}
            />

            <Reactable
                columns={HolidaySetupColumns}
                data={HolidaySetupsData}
                setData={setHolidaySetupsData}
                setTotalCount={setTotalRecords}
                url={HOLIDAY_SETUP} // Use holiday endpoint
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={editPermission ? "sm" : null}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={!!draftPermission}
                isDateRequired={false}
            />

            {/* Add/Edit Modal with holiday fields */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onDraft={handleDraft}
                onSave={handleSave}
                title={editMode ? "Edit Holiday" : "Add Holiday"}
                closeLabel="Cancel"
                saveLabel={editMode || editId ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={editMode}
                loading={loading}
                width="500px" // Increased width for better layout
            >
                <form className="space-y-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Branch: <span className="font-medium text-gray-800 dark:text-gray-100">{configBranchLabel()}</span>
                    </p>
                    {/* Holiday Name */}
                    <div className="space-y-2">
                        <label className={cn(modalFieldLabelClass, "flex items-center gap-1")}>
                            Holiday Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.holiday_name}
                            onChange={(e) => handleInputChange("holiday_name", e.target.value)}
                            placeholder="Enter holiday name"
                            required
                            disabled={loading}
                            className={cn(
                                "w-full rounded-lg transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                        />
                    </div>

                    {/* Holiday Type */}
                    <div className="space-y-2">
                        <label className={cn(modalFieldLabelClass, "flex items-center gap-1")}>
                            Holiday Type <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.holiday_type || null}
                            onChange={(value) => handleInputChange("holiday_type", value)}
                            options={holidaytype}
                            placeholder="Select holiday type"
                            disabled={loading}
                            className={cn(
                                "w-full rounded-lg transition-colors duration-200",
                                "border-gray-300 dark:border-gray-600",
                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                            )}
                            size="large"
                        />
                    </div>

                    {/* Date */}
                    <div className="space-y-2">
                        <label className={cn(modalFieldLabelClass, "flex items-center gap-1")}>
                            Date <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                            value={formData.date}
                            onChange={(date) => handleInputChange("date", date)}
                            format="DD-MM-YYYY"
                            className="w-full"
                            disabled={loading}
                            placeholder="Select holiday date"
                        />
                    </div>

                    {/* Repeat every year */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <div className="flex flex-col">
                            <label className={modalFieldLabelClass}>
                                Repeat every year
                            </label>
                            <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                Applies on this month/day for all years (best for fixed-date holidays)
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => handleInputChange("is_recurring", !formData.is_recurring)}
                            className={cn(
                                "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200",
                                formData.is_recurring ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                            )}
                            disabled={loading}
                        >
                            <span
                                className={cn(
                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                    formData.is_recurring ? "translate-x-4" : "translate-x-1"
                                )}
                            />
                        </button>
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            Notes
                        </label>
                        <Textarea
                            value={formData.notes}
                            onChange={(e) => handleInputChange("notes", e.target.value)}
                            placeholder="Enter holiday notes or description"
                            disabled={loading}
                            {...modalDescriptionFieldProps}
                            className={modalTextareaClass}
                        />
                    </div>

                    {/* Active Status */}
                    <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                        <label className={modalFieldLabelClass}>
                            Active Status
                        </label>
                        <div className="flex items-center gap-3">
                            <span className={cn(
                                "text-xs font-medium",
                                formData.is_active
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}>
                                {formData.is_active ? "Active" : "Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => handleInputChange("is_active", !formData.is_active)}
                                className={cn(
                                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                    formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                    loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                )}
                                disabled={loading}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                        formData.is_active ? "translate-x-4" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        <span className="text-red-500">*</span> indicates required field
                    </p>
                </form>
            </DialogModal>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleDraftDelete}
                title="Delete Draft Holiday"
                description="Are you sure you want to delete this draft holiday? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default HolidaySetup;